import { Section, Text } from "@react-email/components";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { tLayout } from "../../translations";
import formatTemplate from "../../lib/format-template";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import {
  INSTRUCTION_BY_ID,
  isEmptyHtml,
  resolveInstructionKey,
} from "./instructions";
import type { ReturnShippingInstructionsBlockProps } from "./types";

export function ReturnShippingInstructionsEmailRenderer({
  block,
  formatter,
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnShippingInstructionsBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const t = tLayout(locale, defaultLocale);
  const { getShippingInstructions } = useReturnVariableValues();
  const { carrierCode, hasLogisticLabel } = getShippingInstructions();

  const activeKey = resolveInstructionKey(carrierCode, hasLogisticLabel);
  const definition = INSTRUCTION_BY_ID[activeKey];

  // Merchant override (rich HTML, per locale) takes precedence over the default.
  // Pick the first non-empty one — an emptied editor yields "<p></p>", which
  // must fall back to the default rather than render a blank message.
  const localeOverride = block.content?.[locale]?.[activeKey];
  const defaultOverride = block.content?.[defaultLocale]?.[activeKey];
  const override = !isEmptyHtml(localeOverride)
    ? localeOverride
    : !isEmptyHtml(defaultOverride)
      ? defaultOverride
      : undefined;

  if (override) {
    return (
      <Section className="px-4">
        <div
          className="m-0 text-base leading-[1.5] text-center"
          style={{ color: colors.text }}
          dangerouslySetInnerHTML={{
            __html: formatter
              ? formatter(formatTemplate(override))
              : formatTemplate(override),
          }}
        />
      </Section>
    );
  }

  // Default copy from the block translations (tLayout has no per-key fallback).
  const translated = t(definition.tLayoutKey);
  const text =
    translated === definition.tLayoutKey ? definition.fallback : translated;

  return (
    <Section className="px-4">
      <Text
        className="m-0 text-base leading-[1.5] text-center"
        style={{ color: colors.text }}
      >
        {text}
      </Text>
    </Section>
  );
}
